import { useEffect, useMemo, useState } from 'react';
import { Modale } from '../ui/Modale';
import { MessageErreur } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ErreurApi } from '../../lib/api';
import {
  LIBELLES_DEMI_JOURNEE,
  useGestesClasse,
  type DemiJournee,
  type DetailClasse,
  type FormateurProposable,
  type PromotionProgrammable,
} from '../../lib/useElearning';

const CLASSE_CHAMP =
  'min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 px-3 text-sm focus:border-codialis-sky focus:outline-none disabled:opacity-60';

function aujourdHuiIso(): string {
  // Jour civil de La Réunion (UTC+4, sans heure d'été).
  return new Date(Date.now() + 4 * 3600 * 1000).toISOString().slice(0, 10);
}

function Champ({ libelle, children }: { libelle: string; children: JSX.Element }): JSX.Element {
  return (
    <label className="block min-w-0">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wide text-codialis-navy">{libelle}</span>
      {children}
    </label>
  );
}

/**
 * Programmer une classe à distance, ou corriger celle qui l'est (CDC §7.2).
 * La classe s'adresse à une promotion ; ses apprenants en formation y sont
 * attendus. Date et horaires ne bougent plus une fois la diffusion ouverte :
 * c'est l'API qui le dit, l'écran ne fait que griser.
 */
export function ModaleClasse({
  ouverte,
  onFermer,
  promotions,
  formateurs,
  classe,
  onProgrammee,
}: {
  ouverte: boolean;
  onFermer: () => void;
  promotions: PromotionProgrammable[];
  formateurs: FormateurProposable[];
  /** Classe à corriger ; absente, la fenêtre en programme une nouvelle. */
  classe?: DetailClasse | null;
  onProgrammee?: (seanceId: string) => void;
}): JSX.Element {
  const toast = useToast();
  const gestes = useGestesClasse();
  const [promotion, setPromotion] = useState('');
  const [date, setDate] = useState(aujourdHuiIso());
  const [demiJournee, setDemiJournee] = useState<DemiJournee>('MATIN');
  const [heureDebut, setHeureDebut] = useState('08:30');
  const [heureFin, setHeureFin] = useState('12:00');
  const [libelle, setLibelle] = useState('');
  const [bloc, setBloc] = useState('');
  const [formateur, setFormateur] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const choisie = useMemo(() => promotions.find((option) => option.cle === promotion) ?? null, [promotions, promotion]);
  // Seuls les formateurs de l'antenne de la classe peuvent l'ouvrir ; celui
  // déjà confié reste affiché pour ne pas être retiré sans le vouloir.
  const antenne = classe?.siteCode ?? choisie?.siteCode ?? null;
  const proposes = formateurs.filter(
    (option) => (antenne !== null && option.sites.includes(antenne)) || option.id === classe?.formateurId,
  );
  const blocs = classe ? classe.blocs : (choisie?.blocs ?? []);
  const horairesFiges = Boolean(classe?.refus.modificationHoraires);

  useEffect(() => {
    if (!ouverte) return;
    setErreur(null);
    if (classe) {
      setDate(classe.date);
      setDemiJournee(classe.demiJournee);
      setHeureDebut(classe.heureDebut);
      setHeureFin(classe.heureFin);
      setLibelle(classe.libelle);
      setBloc(classe.bloc?.id ?? '');
      setFormateur(classe.formateurId ?? '');
    } else {
      setPromotion((actuelle) => actuelle || promotions[0]?.cle || '');
      setDate(aujourdHuiIso());
      setLibelle('');
      setBloc('');
      setFormateur('');
    }
  }, [ouverte, classe, promotions]);

  // Les horaires proposés sont ceux de la formation, matin ou après-midi.
  useEffect(() => {
    if (classe || !choisie) return;
    setHeureDebut(choisie.horaires[demiJournee].debut);
    setHeureFin(choisie.horaires[demiJournee].fin);
  }, [classe, choisie, demiJournee]);

  const enregistrer = async () => {
    setErreur(null);
    try {
      if (classe) {
        await gestes.modifier.mutateAsync({
          seanceId: classe.seanceId,
          saisie: {
            libelle: libelle.trim() || classe.libelle,
            moduleFormationId: bloc || null,
            ...(horairesFiges ? {} : { date, demiJournee, heureDebut, heureFin }),
            ...(formateurs.length > 0 ? { formateurId: formateur || null } : {}),
          },
        });
        toast('Classe mise à jour');
      } else {
        const reponse = (await gestes.programmer.mutateAsync({
          promotion,
          date,
          demiJournee,
          heureDebut,
          heureFin,
          ...(libelle.trim() ? { libelle: libelle.trim() } : {}),
          moduleFormationId: bloc || null,
          ...(formateur ? { formateurId: formateur } : {}),
        })) as { classe: DetailClasse };
        toast('Classe programmée · les apprenants sont prévenus');
        onProgrammee?.(reponse.classe.seanceId);
      }
      onFermer();
    } catch (cause) {
      setErreur(cause instanceof ErreurApi ? cause.message : 'La classe n’a pas pu être enregistrée.');
    }
  };

  const enCours = gestes.programmer.isPending || gestes.modifier.isPending;

  return (
    <Modale
      titre={classe ? 'Modifier la classe à distance' : 'Programmer une classe à distance'}
      sousTitre={
        classe
          ? `${classe.promotion.libelle} · ${classe.attendus} apprenant(s) attendu(s)`
          : 'Les apprenants en formation de la promotion sont attendus et prévenus.'
      }
      ouverte={ouverte}
      onFermer={onFermer}
      largeur="max-w-xl"
    >
      <form
        className="space-y-4 p-4 sm:p-6"
        onSubmit={(evenement) => {
          evenement.preventDefault();
          void enregistrer();
        }}
      >
        {!classe && (
          <Champ libelle="Promotion">
            <select
              value={promotion}
              onChange={(e) => {
                setPromotion(e.target.value);
                setFormateur('');
              }}
              className={CLASSE_CHAMP}
              required
            >
              {promotions.length === 0 && <option value="">Aucune promotion en formation</option>}
              {promotions.map((option) => (
                <option key={option.cle} value={option.cle}>
                  {option.libelle} · {option.siteCode} · {option.effectif} apprenant{option.effectif > 1 ? 's' : ''}
                </option>
              ))}
            </select>
          </Champ>
        )}
        <Champ libelle="Bloc du programme">
          <select value={bloc} onChange={(e) => setBloc(e.target.value)} className={CLASSE_CHAMP}>
            <option value="">Hors programme</option>
            {blocs.map((option) => (
              <option key={option.id} value={option.id}>
                {option.libelle}
              </option>
            ))}
          </select>
        </Champ>
        <Champ libelle="Intitulé">
          <input
            type="text"
            value={libelle}
            onChange={(e) => setLibelle(e.target.value)}
            placeholder={blocs.find((option) => option.id === bloc)?.libelle ?? 'Classe à distance'}
            maxLength={160}
            className={CLASSE_CHAMP}
          />
        </Champ>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Champ libelle="Date">
            <input
              type="date"
              value={date}
              min={aujourdHuiIso()}
              onChange={(e) => setDate(e.target.value)}
              disabled={horairesFiges}
              required
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Demi-journée">
            <select
              value={demiJournee}
              onChange={(e) => setDemiJournee(e.target.value as DemiJournee)}
              disabled={horairesFiges}
              className={CLASSE_CHAMP}
            >
              {(Object.keys(LIBELLES_DEMI_JOURNEE) as DemiJournee[]).map((valeur) => (
                <option key={valeur} value={valeur}>
                  {LIBELLES_DEMI_JOURNEE[valeur]}
                </option>
              ))}
            </select>
          </Champ>
          <Champ libelle="Début">
            <input
              type="time"
              value={heureDebut}
              onChange={(e) => setHeureDebut(e.target.value)}
              disabled={horairesFiges}
              required
              className={CLASSE_CHAMP}
            />
          </Champ>
          <Champ libelle="Fin">
            <input
              type="time"
              value={heureFin}
              onChange={(e) => setHeureFin(e.target.value)}
              disabled={horairesFiges}
              required
              className={CLASSE_CHAMP}
            />
          </Champ>
        </div>
        {horairesFiges && <p className="text-xs text-codialis-gray">{classe?.refus.modificationHoraires}</p>}
        {formateurs.length > 0 && (
          <Champ libelle="Formateur">
            <select value={formateur} onChange={(e) => setFormateur(e.target.value)} className={CLASSE_CHAMP}>
              <option value="">Celui qui ouvrira la salle</option>
              {proposes.map((option) => (
                <option key={option.id} value={option.id}>
                  {antenne !== null && !option.sites.includes(antenne) ? `${option.nom} (autre antenne)` : option.nom}
                </option>
              ))}
            </select>
          </Champ>
        )}
        {erreur && <MessageErreur message={erreur} />}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onFermer}
            className="min-h-[44px] rounded-xl border border-gray-200 bg-white px-4 text-sm font-semibold text-codialis-navy"
          >
            Annuler
          </button>
          <button
            type="submit"
            disabled={enCours || (!classe && !promotion)}
            className="min-h-[44px] rounded-xl bg-codialis-navy px-4 text-sm font-semibold text-white transition hover:bg-codialis-deep disabled:opacity-60"
          >
            {enCours ? 'Enregistrement…' : classe ? 'Enregistrer' : 'Programmer'}
          </button>
        </div>
      </form>
    </Modale>
  );
}
